import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          // Verify token is still valid
          const response = await authAPI.getMe();
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
          // Token invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  // Direct login with token and user data (for signup flows)
  const loginWithToken = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const register = async (name, email, password, role) => {
    const response = await authAPI.register({ name, email, password, role });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const isAdmin = () => user?.role === 'Admin';
  const isSalesRep = () => user?.role === 'SalesRep';
  const isBackOffice = () => user?.role === 'BackOffice';
  const isPlatformOwner = () => user?.is_platform_owner === true;
  const canManageUsers = () => user?.role === 'Admin';
  const canManagePipeline = () => ['Admin', 'BackOffice'].includes(user?.role);
  const canViewReports = () => ['Admin', 'BackOffice'].includes(user?.role);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithToken,
        register,
        logout,
        isAdmin,
        isSalesRep,
        isBackOffice,
        isPlatformOwner,
        canManageUsers,
        canManagePipeline,
        canViewReports,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
